import { css, html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type {
    Commerce,
    CommerceShareResource,
    CommerceStorefrontExperience,
} from "@teqtank/commerce-sdk";
import "../share/teqtank-qr.js";

type AffiliateTool = "storefront" | "cart" | "routine" | "qr" | "contact" | "collection";

@customElement("teqtank-affiliate-tools")
export class TeqtankAffiliateTools extends LitElement {
    @property({ attribute: false }) public accessor commerce: Commerce | undefined;
    @property() public accessor tools = "storefront cart routine qr contact collection";
    @property({ attribute: "routine-id" }) public accessor routineId = "";
    @property({ attribute: "collection-id" }) public accessor collectionId = "";
    @property({ attribute: "collection-label" }) public accessor collectionLabel = "My picks";
    @property({ type: Boolean, attribute: "show-profile" }) public accessor showProfile = true;
    @state() private accessor storefront: CommerceStorefrontExperience | undefined;
    @state() private accessor busy: AffiliateTool | undefined;
    @state() private accessor qrResource: CommerceShareResource | undefined;
    @state() private accessor error = "";
    private unsubscribe: (() => void) | undefined;

    public static override styles = css`
        :host {
            display: block;
            container-type: inline-size;
            font-family: var(--teqtank-font-body, Inter, ui-sans-serif, system-ui, sans-serif);
            color: var(--teqtank-color-text, #24231f);
        }
        .panel {
            display: grid;
            grid-template-columns: var(--teqtank-affiliate-tools-columns, 1fr);
            gap: 1rem;
            padding: clamp(1rem, 3cqi, 1.5rem);
            border: 1px solid var(--teqtank-color-border, #ddd7cc);
            border-radius: 1.25rem;
            background: linear-gradient(
                145deg,
                var(--teqtank-color-surface, #fff),
                var(--teqtank-color-surface-soft, #f5f1e9)
            );
            box-shadow: 0 1.25rem 3rem rgb(27 38 31 / 9%);
        }
        .profile {
            display: flex;
            align-items: center;
            gap: 0.8rem;
        }
        .avatar {
            display: grid;
            place-items: center;
            width: 2.75rem;
            height: 2.75rem;
            border-radius: 50%;
            background: var(--teqtank-color-accent, #173f35);
            color: var(--teqtank-color-on-accent, #fff);
            font-weight: 800;
        }
        strong,
        small {
            display: block;
        }
        small {
            color: var(--teqtank-color-text-muted, #68645d);
        }
        .tools {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 0.6rem;
        }
        button {
            min-height: 3.25rem;
            padding: 0.75rem;
            border: 1px solid var(--teqtank-color-border, #d6d0c6);
            border-radius: 0.85rem;
            background: var(
                --teqtank-affiliate-tool-background,
                color-mix(in srgb, var(--teqtank-color-surface, #fff) 88%, transparent)
            );
            color: inherit;
            font: inherit;
            font-weight: 750;
            cursor: pointer;
            transition:
                transform 0.18s ease,
                border-color 0.18s ease,
                background 0.18s ease;
        }
        button:hover {
            transform: translateY(-2px);
            border-color: var(--teqtank-color-accent, #173f35);
            background: var(--teqtank-color-surface, #fff);
        }
        button:focus-visible {
            outline: 3px solid
                color-mix(in srgb, var(--teqtank-color-accent, #173f35) 35%, transparent);
            outline-offset: 2px;
        }
        button:disabled {
            cursor: wait;
            opacity: 0.6;
            transform: none;
        }
        .feedback {
            margin: 0;
            font-size: 0.85rem;
            color: var(--teqtank-color-danger, #9b2d2d);
        }
        .qr {
            display: grid;
            place-items: center;
            padding: 0.75rem;
            border-radius: 1rem;
            background: var(--teqtank-color-surface, #fff);
        }
        @container (max-width: 560px) {
            .tools {
                grid-template-columns: repeat(2, minmax(0, 1fr));
            }
        }
        @container (max-width: 340px) {
            .tools {
                grid-template-columns: 1fr;
            }
        }
        @media (prefers-reduced-motion: reduce) {
            button {
                transition: none;
            }
        }
    `;

    public override connectedCallback(): void {
        super.connectedCallback();
        this.bind();
    }
    protected override updated(changed: Map<PropertyKey, unknown>): void {
        if (changed.has("commerce")) this.bind();
    }
    public override disconnectedCallback(): void {
        this.unsubscribe?.();
        super.disconnectedCallback();
    }
    protected override render() {
        const contact = this.storefront?.contact;
        const tools = this.toolList();
        if (!contact && this.showProfile) return html``;
        const initials = contact
            ? contact.displayName
                  .split(/\s+/u)
                  .slice(0, 2)
                  .map((value) => value[0])
                  .join("")
                  .toUpperCase()
            : "↗";
        return html`<section class="panel" part="panel" aria-label="Affiliate sharing tools">
            ${
                this.showProfile && contact
                    ? html`<div class="profile" part="profile">
                          <span class="avatar" part="avatar">${initials}</span
                          ><span part="identity"
                              ><strong part="name">${contact.displayName}</strong
                              ><small part="role">${contact.role}</small></span
                          >
                      </div>`
                    : nothing
            }
            <div class="tools" part="tools">
                ${tools.map((tool) => html`<button part="tool ${tool}" type="button" ?disabled=${Boolean(this.busy)} @click=${() => this.activate(tool)}>${this.label(tool)}</button>`)}
            </div>
            ${this.error ? html`<p class="feedback" part="feedback" role="status">${this.error}</p>` : nothing}
            ${this.qrResource ? html`<div class="qr" part="qr"><teqtank-qr part="qr-code" .src=${this.qrResource.qrCodeUrl} .value=${this.qrResource.url} label="Scan to open shared storefront"></teqtank-qr></div>` : nothing}
        </section>`;
    }

    private toolList(): AffiliateTool[] {
        return this.tools
            .split(/[\s,]+/u)
            .filter((value): value is AffiliateTool =>
                ["storefront", "cart", "routine", "qr", "contact", "collection"].includes(value),
            );
    }
    private label(tool: AffiliateTool): string {
        return {
            storefront: "Share site",
            cart: "Share cart",
            routine: "Share routine",
            qr: "Create QR",
            contact: "Business card",
            collection: this.collectionLabel,
        }[tool];
    }
    private async activate(tool: AffiliateTool): Promise<void> {
        if (!this.commerce) return;
        this.busy = tool;
        this.error = "";
        try {
            const resource =
                tool === "cart"
                    ? await this.commerce.share.cart()
                    : tool === "routine"
                      ? await this.commerce.share.routine(this.routineId)
                      : tool === "contact"
                        ? await this.commerce.share.contact()
                        : tool === "collection"
                          ? await this.commerce.share.collection(this.collectionId)
                          : await this.commerce.share.storefront();
            this.qrResource = tool === "qr" ? resource : undefined;
            this.dispatchEvent(
                new CustomEvent("teqtank-affiliate-tool-activated", {
                    detail: { tool, resource },
                    bubbles: true,
                    composed: true,
                }),
            );
            if (tool !== "qr") await this.commerce.share.native(resource).catch(() => false);
        } catch (error) {
            this.error =
                error instanceof Error
                    ? error.message
                    : "This sharing tool is unavailable right now.";
        } finally {
            this.busy = undefined;
        }
    }
    private bind(): void {
        this.unsubscribe?.();
        if (!this.commerce) return;
        this.storefront = this.commerce.storefront.current;
        this.unsubscribe = this.commerce.storefront.onChanged((event) => {
            this.storefront = event.storefront;
        });
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "teqtank-affiliate-tools": TeqtankAffiliateTools;
    }
}
